import { findThreadsInDocument } from '@tiptap-pro/extension-comments';
import type { TCollabThread } from '@tiptap-pro/provider';
import type { Editor } from '@tiptap/core';
import { type RefObject, useCallback, useLayoutEffect, useRef, useState } from 'react';

const CARD_GAP = 10;
const DEFAULT_CARD_HEIGHT = 80;

function resolveOverlaps(anchors: Array<{ id: string; y: number }>, heights: Map<string, number>): Map<string, number> {
  const sorted = anchors.toSorted((a, b) => a.y - b.y);
  const result = new Map<string, number>();
  let nextMinTop = 0;

  for (const { id, y } of sorted) {
    const top = Math.max(y, nextMinTop);
    result.set(id, top);
    nextMinTop = top + (heights.get(id) ?? DEFAULT_CARD_HEIGHT) + CARD_GAP;
  }

  return result;
}

export interface ThreadPositionState {
  positions: Map<string, number>;
  orphanedIds: Set<string>;
}

const EMPTY_STATE: ThreadPositionState = { positions: new Map(), orphanedIds: new Set() };

export function useThreadPositions(
  editor: Editor,
  threads: TCollabThread[],
  cardRefs: RefObject<Map<string, HTMLDivElement>>,
  listRef: RefObject<HTMLDivElement | null>,
): ThreadPositionState {
  const [state, setState] = useState<ThreadPositionState>(EMPTY_STATE);
  const rafRef = useRef(0);

  const calculate = useCallback(() => {
    if (!editor || editor.isDestroyed) return;

    const listTop = listRef.current?.getBoundingClientRect().top ?? editor.view.dom.getBoundingClientRect().top;
    const threadIds = new Set(threads.map((t) => t.id));
    const seen = new Set<string>();
    const anchors: Array<{ id: string; y: number }> = [];

    for (const t of findThreadsInDocument(editor, { blockTypeName: 'blockThread', markTypeName: 'inlineThread' })) {
      if (seen.has(t.thread) || !threadIds.has(t.thread)) continue;
      seen.add(t.thread);

      try {
        anchors.push({ id: t.thread, y: Math.max(0, editor.view.coordsAtPos(t.pos).top - listTop) });
      } catch {
        /* position may be invalid during editor updates */
      }
    }

    const nextOrphaned = new Set<string>();
    for (const id of threadIds) {
      if (!seen.has(id)) nextOrphaned.add(id);
    }

    const heights = new Map<string, number>();
    cardRefs.current?.forEach((el, id) => heights.set(id, el.offsetHeight));

    const nextPositions = resolveOverlaps(anchors, heights);

    let tail = 0;
    for (const [id, top] of nextPositions) {
      tail = Math.max(tail, top + (heights.get(id) ?? DEFAULT_CARD_HEIGHT) + CARD_GAP);
    }
    for (const id of nextOrphaned) {
      nextPositions.set(id, tail);
      tail += (heights.get(id) ?? DEFAULT_CARD_HEIGHT) + CARD_GAP;
    }

    setState((prev) => {
      const positionsChanged =
        prev.positions.size !== nextPositions.size ||
        [...nextPositions].some(([id, top]) => prev.positions.get(id) !== top);

      const orphanedChanged =
        prev.orphanedIds.size !== nextOrphaned.size || [...nextOrphaned].some((id) => !prev.orphanedIds.has(id));

      return positionsChanged || orphanedChanged ? { positions: nextPositions, orphanedIds: nextOrphaned } : prev;
    });
  }, [editor, threads, cardRefs, listRef]);

  useLayoutEffect(() => {
    if (editor.isDestroyed) return;

    calculate();

    const schedule = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(calculate);
    };

    editor.on('transaction', schedule);

    document.addEventListener('scroll', schedule, { passive: true, capture: true });

    const observer = new ResizeObserver(schedule);
    if (!editor.isDestroyed) observer.observe(editor.view.dom);
    cardRefs.current?.forEach((el) => observer.observe(el));

    return () => {
      cancelAnimationFrame(rafRef.current);
      editor.off('transaction', schedule);
      document.removeEventListener('scroll', schedule, { capture: true });
      observer.disconnect();
    };
  }, [editor, threads, calculate, state.positions.size]);

  return state;
}
